import React, { useRef, useState } from "react";
import ActionsBar from "components/SubHeader/ActionsBar";
import SubHeader from "components/SubHeader";
import SubHeaderTitle from "components/SubHeader/SubHeaderTitle";
import { useTranslation } from "react-i18next";
import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";
import { TCity, TJurisdiction, TProvince } from "types/dto";
import { toast } from "react-toastify";
import { useURLTools } from "utils/URLtools";
import SubHeaderFilters from "components/SubHeader/SubHeadeFilters";
import { AddApartmentModal, TAddFormTypes } from "./AddAparmentModal";
import { EditApartmentModal, TEditFormTypes } from "./EditApartmentModal";
import RegionAutoComplete from "components/SpecificAutoComplete/RegionAutoComplete";
import ApartmentsGrid from "components/Grid/grids/ApartmentsGrid";
import { DataGridRef } from "devextreme-react/data-grid";
import { TApartmentGridRow } from "components/Grid/grids/ApartmentsGrid/types";
import { TResponseCode } from "utils/netCall/types";
import { usePermissions } from "components/Permission/usePermissions";

const ApartmentsView = () => {
  const { t } = useTranslation();
  const { queryParams, addUpdateRemoveQueryParams, navigateWithQueryParams } =
    useURLTools();
  const { hasPermission } = usePermissions();
  const [isSubmitting, setIsSubmiting] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TJurisdiction>();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const GridRef = useRef<DataGridRef<any, any>>(null);

  const onAddClick = () => {
    setAddModalOpen(true);
  };
  const onEditClick = (data: any) => {
    if (!hasPermission(["d_apartment", "superadmin"])) {
      toast.error(t("youDontHavePermissionToEdit"));
      return;
    }
    setSelectedRow(data);
    setIsEditModalOpen(true);
  };

  const onSubmitAddApartment = async (values: TAddFormTypes) => {
    setIsSubmiting(true);
    const res = await netCall<TProvince>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/Apartment/AddApartment`,
      body: {
        ...values,
        latitude: values.location?.lat,
        longitude: values.location?.lng,
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      setIsSubmiting(false);
      setAddModalOpen(false);
      toast.success(t("operationSuccessful"));
      GridRef.current?.instance().refresh();
    } else {
      setIsSubmiting(false);
      toast.error(t("operationFailed"));
    }
  };
  const onSubmitEditApartments = async (values: TEditFormTypes) => {
    if (selectedRow) {
      setIsSubmiting(true);
      const res = await netCall<TCity>({
        method: "POST",
        endPoint: `${ManagementApiUrl()}/api/Apartment/UpdateApartment`,
        body: {
          id: selectedRow.id,
          ...values,
          latitude: values.location?.lat,
          longitude: values.location?.lng,
        },
      });
      if (res.responseCode === TResponseCode.OK) {
        setIsSubmiting(false);
        toast.success(t("operationSuccessful"));
        setIsEditModalOpen(false);
        setSelectedRow(undefined);
        GridRef.current?.instance().refresh();
      } else {
        setIsSubmiting(false);
        toast.error(t("operationFailed"));
      }
    }
  };

  const onApartmentsGridRowClick = (row: TApartmentGridRow) => {
    navigateWithQueryParams(`/dashboard/apartments/details/${row.id}`, [], {
      replace: true,
    });
  };

  return (
    <>
      <SubHeader>
        <SubHeaderTitle>{t("apartments")}</SubHeaderTitle>
        <ActionsBar
          requestTime={0}
          isLoading={false}
          actionButtons={[
            {
              iconName: "MdAdd",
              buttonTooltip: "افزودن",
              action: onAddClick,
              permissions: ["superadmin", "d_apartment"],
            },
          ]}
        />
      </SubHeader>
      <SubHeaderFilters>
        <RegionAutoComplete
          onChange={(value) => {
            addUpdateRemoveQueryParams([
              { parameter: "provinceId", value: value?.province?.id || "" },
              { parameter: "cityId", value: value?.city?.id || "" },
              {
                parameter: "jurisdictionId",
                value: value?.jurisdiction?.id || "",
              },
            ]);
          }}
          defaultValue={{
            provinceId: queryParams?.provinceId,
            cityId: queryParams?.cityId,
            jurisdictionId: queryParams?.jurisdictionId,
          }}
        />
      </SubHeaderFilters>

      <div className={"w-full flex gap-x h-[calc(100%-122px)]"}>
        <ApartmentsGrid
          enableDetailsContextMenu
          provinceId={queryParams?.provinceId}
          cityId={queryParams?.cityId}
          jurisdictionId={queryParams?.jurisdictionId}
          actionButtons={[
            {
              icon: "fa-solid fa-pencil",
              hint: t("edit"),
              onClick: onEditClick,
            },
          ]}
          isLoading={false}
          ref={GridRef}
          onRowClick={onApartmentsGridRowClick}
        />
      </div>
      {addModalOpen && (
        <AddApartmentModal
          onClose={() => setAddModalOpen(false)}
          isOpen={addModalOpen}
          onSubmit={onSubmitAddApartment}
          isLoading={isSubmitting}
        />
      )}
      {isEditModalOpen && (
        <EditApartmentModal
          apartmentId={selectedRow?.id || ""}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedRow(undefined);
          }}
          onSubmit={onSubmitEditApartments}
          isLoading={isSubmitting}
        />
      )}
    </>
  );
};

export default ApartmentsView;
